/**
 * frontend/src/pages/valuation/_SignOffReview.tsx
 *
 * VALUATION-B3 sign-off — the review panel. What is about to be frozen, and
 * once it is frozen, what was.
 *
 * Two components, deliberately not one:
 *
 *   * `PendingReview` reads the LIVE surfaces — the tuning inputs carried
 *     forward, the `/preflight` computation, the active config. It is a
 *     preview, and every number on it can still move.
 *   * `FrozenReview` reads the SNAPSHOT and nothing else. Not the config
 *     endpoint, not preflight — the snapshot carries its own frozen config, and
 *     a signed record that rendered off the live config would silently restate
 *     itself the next time somebody activated a new methodology version. That
 *     is the whole point of freezing it.
 *
 * The four things the valuator is asked to review before signing are the four
 * things a reader of the deliverable would challenge later: the weights, the
 * rationale for moving them, the driver scores, and which benchmark version
 * priced the multiple.
 *
 * Split out of ValuationSignOff.tsx under docs/FILE_SIZE_POLICY.md.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CollapsibleTitle, useCollapsible } from './_CollapsibleCard'
import type {
  BasisBlock,
  ComputedPayload,
  ConfigResponse,
  RecomputeBody,
  SnapshotBody,
} from '@/types/valuationB3'
import { Fact, basisLabel, snapshotStatusLabel } from './_shared'
import { convergenceLabel } from './_convergenceLabel'
import { ROLE_LABELS } from '@/components/AppShell'
import { fracPct, money, multiple } from '@/utils/format'
import { formatDateTime } from '@/utils/date'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-pal-ink-muted">{title}</h3>
      {children}
    </div>
  )
}

// ── Before the signature ─────────────────────────────────────────────────────

export function PendingReview({
  body,
  basis,
  computed,
  config,
}: {
  body: RecomputeBody
  basis: BasisBlock | null
  computed: ComputedPayload | null
  config: ConfigResponse | null
}) {
  const defaults = config?.config.triangulation_weights
  const incomeWeight = body.tuning?.income_weight ?? defaults?.income_weight ?? null
  const marketWeight = body.tuning?.market_weight ?? defaults?.market_weight ?? null
  const tuned = basis?.tuned ?? !!body.tuning
  const rationale = body.tuning?.rationale ?? body.rationale ?? ''
  const range = computed?.computed_range ?? null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review before signing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-xs text-pal-ink-muted">
          These are the inputs the signature will freeze. Nothing here is stored yet — a preview
          is not a record. Change anything by going back to Methodology.
        </p>

        <Section title="Approach weights">
          <Fact label="Income approach" value={fracPct(incomeWeight)} mono />
          <Fact label="Market approach" value={fracPct(marketWeight)} mono />
          <Fact
            label="Source"
            value={tuned ? 'Tuned for this engagement' : 'Signed methodology default'}
          />
        </Section>

        <Section title="Methodology reason">
          {rationale.trim()
            ? <p className="whitespace-pre-wrap text-sm text-gray-800">{rationale}</p>
            : tuned
              ? (
                <p className="text-sm text-pal-warning-text">
                  Not recorded. A tuned methodology cannot be signed without one.
                </p>
              )
              : (
                <p className="text-sm text-gray-800">
                  None needed: this engagement uses the signed methodology default.
                </p>
              )}
        </Section>

        <Section title="Benchmark version">
          <Fact label="Industry" value={range?.benchmark_label ?? body.benchmark_key} />
          <Fact label="Basis" value={range ? basisLabel(range.basis) : '—'} mono />
          <Fact
            label="Range"
            value={range ? `${multiple(range.low)} – ${multiple(range.high)}` : '—'}
            mono
          />
          <Fact label="Table refreshed" value={range?.refreshed_on ?? '—'} mono />
          <Fact label="Source" value={range?.source_label ?? range?.source ?? '—'} />
          {range?.provisional && (
            <p className="mt-2 text-xs text-pal-warning-text">
              PROVISIONAL — this benchmark row is not Valuation Signer co-signed. It will be frozen as
              provisional and the deliverable says so.
            </p>
          )}
        </Section>

        {computed && (
          <Section title="Resulting value">
            <Fact label="Concluded multiple" value={multiple(computed.concluded_multiple)} mono />
            <Fact
              label="Band"
              value={`${money(computed.value_low)} – ${money(computed.value_high)}`}
              mono
            />
            <Fact label="Convergence" value={convergenceLabel(computed.convergence_status)} />
          </Section>
        )}
      </CardContent>
    </Card>
  )
}

// ── After the signature ──────────────────────────────────────────────────────

export function FrozenReview({
  snapshot,
  valueKind,
  signedByName,
  multipleSourceLabel,
  benchmarkSourceLabel,
  collapsible = false,
}: {
  collapsible?: boolean
  snapshot: SnapshotBody
  /** Display words from the server; the snapshot stays as frozen. */
  signedByName?: string
  multipleSourceLabel?: string
  benchmarkSourceLabel?: string
  /** From the engagement — the snapshot does not carry it. */
  valueKind?: 'concluded' | 'calculated' | null
}) {
  const weights = snapshot.config.triangulation_weights
  const c = useCollapsible(collapsible)
  return (
    <Card {...c.cardProps}>
      <CardHeader>
        <CollapsibleTitle collapsible={collapsible} open={c.open} titleId={c.titleId} onToggle={c.toggle}>Saved record</CollapsibleTitle>
      </CardHeader>
      {c.open && <CardContent className="space-y-5">
        <p className="text-xs text-pal-ink-muted">
          These figures come from the inputs saved when this valuation was finalized. Later updates
          to the valuation method do not change them.
        </p>

        <Section title="Signature">
          <Fact label="Signed by" value={signedByName ?? snapshot.signed_by} />
          <Fact label="Role" value={ROLE_LABELS[snapshot.signed_role] ?? snapshot.signed_role} mono />
          <Fact label="Signed on" value={formatDateTime(snapshot.signed_on)} mono />
          <Fact label="Status" value={snapshotStatusLabel(snapshot.status)} mono />
          {/* JOIN-MDEMO 3b: owner-approved, no version number (OWNER_APPROVALS.log 2026-10-25 20:19-20:22). */}
          {snapshot.supersedes !== null && (
            <div className="py-1.5 text-xs text-gray-500">Replaces the earlier record</div>
          )}
        </Section>

        <Section title="Approach weights">
          <Fact label="Income approach" value={fracPct(weights.income_weight)} mono />
          <Fact label="Market approach" value={fracPct(weights.market_weight)} mono />
        </Section>

        <Section title="Rationale">
          <p className="whitespace-pre-wrap text-sm text-gray-800">{snapshot.rationale}</p>
        </Section>

        <Section title="Benchmark version">
          <Fact label="Industry" value={snapshot.benchmark_label} />
          <Fact label="Basis" value={basisLabel(snapshot.basis)} mono />
          <Fact
            label="Range"
            value={`${multiple(snapshot.range_low)} – ${multiple(snapshot.range_high)}`}
            mono
          />
          <Fact label="Table refreshed" value={snapshot.benchmark_refreshed_on} mono />
          <Fact label="Source" value={benchmarkSourceLabel ?? snapshot.benchmark_source} />
          {snapshot.benchmark_provisional && (
            <p className="mt-2 text-xs text-amber-800">
              PROVISIONAL benchmark row — frozen as such.
            </p>
          )}
        </Section>

        <Section title="Frozen value">
          <Fact label="Concluded multiple" value={multiple(snapshot.concluded_multiple)} mono />
          <Fact label="Multiple source" value={multipleSourceLabel ?? snapshot.multiple_source} />
          {snapshot.clamp_note && (
            <p className="mt-1 text-xs text-amber-800">{snapshot.clamp_note}</p>
          )}
          <Fact
            label="Band"
            value={`${money(snapshot.value_low)} – ${money(snapshot.value_high)}`}
            mono
          />
          <Fact
            label={valueKind === 'concluded' ? 'Concluded value' : 'Calculated value'}
            value={money(snapshot.calculated_value)}
            mono
          />
          <Fact label="Convergence" value={convergenceLabel(snapshot.convergence_status)} />
        </Section>

        {snapshot.warnings.length > 0 && (
          <Section title="Warnings frozen with the record">
            <ul className="list-disc space-y-1 pl-5 text-xs text-pal-pill-warning-text">
              {snapshot.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </Section>
        )}
      </CardContent>}
    </Card>
  )
}
